import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

interface PasswordInputProps {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  validationState?: 'idle' | 'valid' | 'invalid';
  error?: string;
  success?: string;
  autoComplete?: string;
  className?: string;
  showStrengthMeter?: boolean;
  strength?: number;
  strengthText?: string;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  onFocus,
  placeholder,
  required = false,
  disabled = false,
  validationState = 'idle',
  error,
  success,
  autoComplete,
  className = '',
  showStrengthMeter = false,
  strength = 0,
  strengthText = ''
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const getInputClasses = () => {
    const baseClasses = 'w-full px-4 py-3 bg-white dark:bg-zinc-800 border rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2';

    if (validationState === 'invalid' || error) {
      return `${baseClasses} border-red-300 dark:border-red-600 focus:ring-red-500 focus:border-red-500`;
    }

    if (validationState === 'valid' || success) {
      return `${baseClasses} border-green-300 dark:border-green-600 focus:ring-green-500 focus:border-green-500`;
    }

    if (isFocused) {
      return `${baseClasses} border-teal-300 dark:border-teal-600 focus:ring-teal-500 focus:border-teal-500`;
    }

    return `${baseClasses} border-zinc-300 dark:border-zinc-700 focus:ring-teal-500 focus:border-teal-500`;
  };

  const getIcon = () => {
    if (validationState === 'valid' || success) {
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    }

    if (validationState === 'invalid' || error) {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }

    return null;
  };

  const getStrengthColor = () => {
    if (strength >= 80) return 'text-green-500';
    if (strength >= 60) return 'text-blue-500';
    if (strength >= 40) return 'text-yellow-500';
    if (strength >= 20) return 'text-orange-500';
    return 'text-red-500';
  };

  const getStrengthBgColor = () => {
    if (strength >= 80) return 'bg-green-500';
    if (strength >= 60) return 'bg-blue-500';
    if (strength >= 40) return 'bg-yellow-500';
    if (strength >= 20) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-zinc-700 dark:text-zinc-300"
      >
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className="relative">
        <input
          id={id}
          name={name}
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          className={getInputClasses()}
        />

        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
          {getIcon() && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              {getIcon()}
            </motion.div>
          )}

          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors"
            tabIndex={-1}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Password Strength Meter */}
      {showStrengthMeter && value && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="space-y-2"
        >
          <div className="flex items-center justify-between text-sm">
            <span className="text-zinc-600 dark:text-zinc-400">Password strength:</span>
            <span className={`font-medium ${getStrengthColor()}`}>
              {strengthText}
            </span>
          </div>

          <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2">
            <motion.div
              className={`h-2 rounded-full ${getStrengthBgColor()}`}
              initial={{ width: 0 }}
              animate={{ width: `${strength}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>
      )}

      {/* Error Message */}
      {(error || (validationState === 'invalid')) && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center space-x-2 text-red-600 dark:text-red-400 text-sm"
        >
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </motion.div>
      )}

      {/* Success Message */}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center space-x-2 text-green-600 dark:text-green-400 text-sm"
        >
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>{success}</span>
        </motion.div>
      )}
    </div>
  );
};

export default PasswordInput;
